import React from 'react';
import { LineChart, AlertCircle, CheckCircle, Clock } from 'lucide-react';

/**
 * Onboarding Chart Placeholder Component
 * 
 * Shows an annotated diagram overlay on the chart area during onboarding.
 * Illustrates what the chart will look like after the first 10 runs,
 * with labeled annotations explaining key concepts.
 * 
 * Uses muted colors so CTAs still pop.
 */
const OnboardingChartPlaceholder: React.FC = () => {
  return (
    <div className="h-64 w-full bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 rounded-lg border-2 border-dashed border-gray-200 dark:border-gray-700 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-20">
        <svg viewBox="0 0 400 200" className="w-full h-full">
          {/* Grid lines */}
          <defs>
            <pattern id="grid" width="40" height="20" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 20" fill="none" stroke="currentColor" strokeWidth="0.5"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
          
          {/* Mock chart line */}
          <path
            d="M 50 160 Q 100 140 150 120 T 250 100 Q 300 90 350 80"
            fill="none"
            stroke="#10b981"
            strokeWidth="2"
            opacity="0.6"
          />
          
          {/* Mock data points */}
          <circle cx="50" cy="160" r="3" fill="#10b981" opacity="0.7" />
          <circle cx="100" cy="140" r="3" fill="#10b981" opacity="0.7" />
          <circle cx="150" cy="120" r="3" fill="#10b981" opacity="0.7" />
          <circle cx="200" cy="110" r="3" fill="#ef4444" opacity="0.7" />
          <circle cx="250" cy="100" r="3" fill="#10b981" opacity="0.7" />
          <circle cx="300" cy="90" r="3" fill="#10b981" opacity="0.7" />
          <circle cx="350" cy="80" r="3" fill="#10b981" opacity="0.7" />
        </svg>
      </div>

      {/* Center Content */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="text-center bg-white/90 dark:bg-gray-900/90 p-6 rounded-lg border border-gray-200 dark:border-gray-700 max-w-md">
          <LineChart className="h-8 w-8 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
            Failures Over Time Preview
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            This chart will track your test reliability once you start running tests
          </p>
          
          {/* Annotation Icons */}
          <div className="flex justify-center gap-6 text-xs">
            <div className="flex flex-col items-center">
              <CheckCircle className="h-4 w-4 text-green-500 mb-1" />
              <span className="text-muted-foreground">Test Run</span>
            </div>
            <div className="flex flex-col items-center">
              <AlertCircle className="h-4 w-4 text-red-500 mb-1" />
              <span className="text-muted-foreground">Alert</span>
            </div>
            <div className="flex flex-col items-center">
              <Clock className="h-4 w-4 text-blue-500 mb-1" />
              <span className="text-muted-foreground">Trend</span>
            </div>
          </div>
        </div>
      </div>

      {/* Floating Annotations */}
      <div className="absolute top-4 left-4 bg-white/95 dark:bg-gray-800/95 px-3 py-2 rounded-md border border-gray-200 dark:border-gray-700 text-xs">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
          <span className="text-muted-foreground">Successful runs</span>
        </div>
      </div>
      
      <div className="absolute top-4 right-4 bg-white/95 dark:bg-gray-800/95 px-3 py-2 rounded-md border border-gray-200 dark:border-gray-700 text-xs">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-red-500 rounded-full"></div>
          <span className="text-muted-foreground">Failure detected</span>
        </div>
      </div>
      
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-blue-50 dark:bg-blue-950 px-3 py-2 rounded-md border border-blue-200 dark:border-blue-800 text-xs">
        <span className="text-blue-700 dark:text-blue-300 font-medium">
          After 10 runs, trends become visible
        </span>
      </div>
    </div>
  );
};

export default OnboardingChartPlaceholder;